import { useContext, useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  TextField,
  Typography,
  Autocomplete,
  IconButton,
} from "@mui/material";

import {
  Chart,
  ChartSeries,
  ChartSeriesItem,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
} from "@progress/kendo-react-charts";
import "hammerjs";

import "@progress/kendo-theme-default/dist/all.css";
import {
  getUsuarios,
  getAnalisis,
  getClientes,
  getConfPlantaClientePorClienteOferta,
  getOfertas,
  getParametros
} from "../api";
import { useOnlineStatus } from "../hooks/useSync";
import { db } from "../libraries/db";
import { useSyncPendingChanges } from '../hooks/useSync';
import { AuthContext } from "../context/AuthContext";
import { useDiagrama } from "../helpers/generarDiagrama";
import { useNodesState, useEdgesState } from "@xyflow/react";
import { DashboardContext } from "../context/DashboardContext";
//Icono para subir al principio de la pagina
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import "./HomeCliente.css";

import { useLocation } from "react-router-dom";
import { getObservacionesByElementoId } from "../api/observacionesElementos";
import FlowWithProvider from "./DiagramaEditableEdges/components/FlowWithProvider";
import { ConnectionLine } from "./DiagramaEditableEdges/edges/ConnectionLine";
import { IncidenciasCard } from "./Home/IncidenciasCard";
import { TareasCalendar } from "./Home/TareasCalendar";
import { ParametersTable } from "./Home/ParametersTable";
import { Graphics } from "./Home/Graphics";
import { Documents } from "./Home/Documents";

const HomeCliente = () => {
  // Guardado de datos
  const [clientes, setClientes] = useState([]);
  const [ofertas, setOfertas] = useState([]);
  const [parametros, setParametros] = useState([]);
  const [analisis, setAnalisis] = useState([]);
  const [plantaActiva, setPlantaActiva] = useState({});

  const [elementoGeneralSeleccionado, setElementoGeneralSeleccionado] = useState(undefined);

  const [clienteSeleccionado, setClienteSeleccionado] = useState({
    id: 0,
    nombreCliente: "",
    codigoCliente: 0,
    oferta: 0,
    descripcion: "",
    referencia: "",
  });

  const [observaciones, setObservaciones] = useState([]);
  const [observacion, setObservacion] = useState({
    id: 0,
    idElemento: 0,
    observacion: "",
    nombreUsuario: "",
    apellidosUsuario: "",
    fecha: null,
    verCliente: true,
    verInsp: true,
  });

  const [observacionEditar, setObservacionEditar] = useState({
    id: 0,
    idElemento: 0,
    observacion: "",
    nombreUsuario: "",
    apellidosUsuario: "",
    fecha: null,
    verCliente: true,
    verInsp: true,
  });

  // Variables para el diagrama
  const [nodos, setNodos, onNodesChange] = useNodesState([]);
  const [lados, setLados, onEdgesChange] = useEdgesState([]);
  const { nodeTypesDashboard } = useDiagrama();

  // Variables de contexto
  const { user } = useContext(AuthContext);
  const isOnline = useOnlineStatus();
  const {
    elementoActivo,
    elementosGeneral,
    parametroActivo,
    analisisActivo,
    analisisParametros,
    parametrosFiltrados,
    setElementoActivo,
    setAnalisisActivo,
    setParametroActivo,
    handleSeleccionarParametro,
    handleCargarTodosElementosPlanta,
    handleSeleccionarAnalisis,
    GetParametrosAnalisisPlanta,
    GetValoresParametros,
    parametrosFiltradosNoFQ,
    GetParametrosFiltradosSinSeleccionarElemento,
    descargarPdf,
    toggleRealizado,
    GetFicheros,
  } = useContext(DashboardContext);


  //Variables filtros
  const [inputCodigoCliente, setInputCodigoCliente] = useState("");
  const [inputNombreCliente, setInputNombreCliente] = useState("");

  const { state } = useLocation();

  // Función auxiliar para obtener planta con soporte offline
  const getPlantaWithOfflineSupport = async (codigoCliente, oferta) => {
    try {
      if (isOnline) {
        // ONLINE: Hacer petición a la API
        return await getConfPlantaClientePorClienteOferta(codigoCliente, oferta);
      } else {
        // OFFLINE: Leer desde base de datos local
        const planta = await db.table('GES_Conf_PlantasCliente')
          .where('[codigoCliente+oferta]')
          .equals([codigoCliente, oferta])
          .first();

        if (planta) {
          console.log('📦 Planta cargada desde BD local (OFFLINE):', planta);
          return planta;
        } else {
          console.warn('⚠️ Planta no encontrada en BD local');
          return null;
        }
      }
    } catch (error) {
      console.error('Error cargando planta:', error);
      // Intenta fallback offline si está online pero hay error
      if (!isOnline) {
        const planta = await db.table('GES_Conf_PlantasCliente')
          .where('[codigoCliente+oferta]')
          .equals([codigoCliente, oferta])
          .filter(planta => !planta.deleted)
          .first();
        return planta || null;
      }
      return null;
    }
  };

  useEffect(() => {
    setElementoActivo({});
    setAnalisisActivo({});
    setElementoGeneralSeleccionado(undefined);
  }, []);

  // Efecto que realiza las peticiones al cargar la página
  useEffect(() => {
    const loadData = async () => {
      try {
        if (isOnline) {
          // ONLINE: Hacer peticiones a la API
          const clientesResp = await getClientes();
          setClientes(clientesResp.filter((cliente) => !cliente.deleted));

          const ofertasResp = await getOfertas();
          setOfertas(ofertasResp.filter((oferta) => !oferta.deleted));

          const parametrosResp = await getParametros();
          setParametros(parametrosResp.filter((param) => !param.deleted));

          const analisisResp = await getAnalisis();
          setAnalisis(analisisResp.filter((an) => !an.deleted));
        } else {
          // OFFLINE: Leer datos de la base de datos local
          const clientesLocal = await db.table('GES_Clientes').toArray();
          setClientes(clientesLocal.filter((cliente) => !cliente.deleted));

          const ofertasLocal = await db.table('GES_OfertasClientes').toArray();
          setOfertas(ofertasLocal.filter((oferta) => !oferta.deleted));

          const parametrosLocal = await db.table('LST_Parametros').toArray();
          setParametros(parametrosLocal.filter((param) => !param.deleted));

          const analisisLocal = await db.table('GES_Analisis').toArray();
          setAnalisis(analisisLocal.filter((an) => !an.deleted));

          console.log('📦 Datos cargados desde base de datos local (OFFLINE)');
        }

        setElementoActivo({});
        setParametroActivo({});
        GetParametrosAnalisisPlanta();
        GetValoresParametros();
        GetFicheros();

      } catch (error) {
        console.error('Error cargando datos:', error);
      }
    };

    loadData();
  }, [isOnline]);

  // Efecto que carga el diagrama cada vez que se cambia de planta
  useEffect(() => {
    if (plantaActiva.diagrama) {
      const datosDiagrama = JSON.parse(plantaActiva.diagrama);
      setNodos(datosDiagrama.nodos);
      setLados(datosDiagrama.lados);
    } else {
      setNodos([]);
      setLados([]);
    }

    setTabActivoElementosGeneral(0);
    handleCargarTodosElementosPlanta(undefined, undefined);
    setElementoGeneralSeleccionado(undefined);
    handleCargarTodosElementosPlanta(plantaActiva.id, plantaActiva.oferta);
  }, [plantaActiva]);

  useEffect(() => {
    if (
      elementoGeneralSeleccionado === undefined &&
      elementosGeneral.length > 0
    ) {
      getObservacionesByElementoId(elementosGeneral[0].id).then((res) => {
        setObservaciones(res);
      });
      GetParametrosFiltradosSinSeleccionarElemento(elementosGeneral[0].id);
      setElementoGeneralSeleccionado(elementosGeneral[0]);
    }
  }, [elementosGeneral]);

  useEffect(() => {
    if (state != null) {
      setClienteSeleccionado((prevParam) => ({
        ...prevParam,
        codigoCliente: state.codigoCliente,
        nombreCliente: state.nombreCliente,
        oferta: state.oferta,
        descripcion: state.descripcion || "",
      }));

      getPlantaWithOfflineSupport(
        state.codigoCliente,
        state.oferta
      ).then((res) => (res ? setPlantaActiva(res) : setPlantaActiva({})));
    }
  }, [state]);

  // Efecto que filtra las tareas al cambiar los datos de planta activa
  useEffect(() => {

    if (elementoActivo.nombre) {
      const scroll = document.getElementById("scroll");
      scroll.scrollIntoView({ behavior: "smooth" });
    }

    // handleCargarTodosElementosPlanta( plantaActiva.id, plantaActiva.oferta);
  }, [plantaActiva, elementoActivo, parametrosFiltrados]);

  useEffect(() => {

    if (analisisActivo.nombre) {
      const scroll = document.getElementById("scroll-2");
      scroll.scrollIntoView({ behavior: "smooth" });
    }
  }, [analisisActivo]);

  useEffect(() => {
    if (clienteSeleccionado.oferta != "" && clientes.length > 0) {
      const oferta = ofertas.filter(
        (ofert) =>
          ofert.numeroOferta === clienteSeleccionado.oferta && !ofert.deleted
      )[0];
      if (oferta) {
        handleSeleccionOferta({
          target: { textContent: oferta.numeroOferta.toString() },
        });
      }
    }
  }, [clienteSeleccionado.oferta]);

  const ChartContainer = () => {
    if (parametroActivo.datos) {
      const rawData = parametroActivo.datos.map((valor, index) => ({
        mes: ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"][index],
        valor: valor === "" ? null : valor, // Si el valor es "", lo cambiamos a null
      }));

      const months = rawData.map((item) => item.mes);
      const values = rawData.map((item) => item.valor);
      return (
        <Chart style={{ height: "500px" }}>
          <ChartCategoryAxis>
            <ChartCategoryAxisItem
              title={{ text: "Meses" }}
              categories={months}
            />
          </ChartCategoryAxis>
          <ChartSeries>
            <ChartSeriesItem type="line" data={values} />
          </ChartSeries>
        </Chart>
      );
    } else {
      return (
        <Chart style={{ height: "500px" }}>
          <ChartCategoryAxis>
            <ChartCategoryAxisItem
              title={{
                text: "Meses",
              }}
              categories={["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"]}
            />
          </ChartCategoryAxis>
          <ChartSeries>
            <ChartSeriesItem type="line" data={parametroActivo.datos} />
          </ChartSeries>
        </Chart>
      );
    }
  };

  // Con esta función, al seleccionar una oferta seteamos la planta activa
  const handleSeleccionOferta = (e) => {
    setElementoActivo({});
    setAnalisisActivo({});

    const ofertaSeleccionada = parseInt(e.target.textContent);

    getPlantaWithOfflineSupport(
      clienteSeleccionado.codigoCliente,
      ofertaSeleccionada
    ).then((res) => (res ? setPlantaActiva(res) : setPlantaActiva({})));
  };

  const handleSeleccionOferta2 = (e) => {
    const ofertaSeleccionada = parseInt(e.target.textContent);

    setClienteSeleccionado({
      ...clienteSeleccionado,
      oferta: e.target.textContent,
    });

    getPlantaWithOfflineSupport(
      clienteSeleccionado.codigoCliente,
      ofertaSeleccionada
    ).then((res) => (res ? setPlantaActiva(res) : setPlantaActiva({})));
  };

  //Contador2 para Parametros del Anaálisis
  const [contadorYear2, setCount2] = useState(new Date().getFullYear());

  const handleIncrementarContador2 = () => {
    setCount2(contadorYear2 + 1);
    //Reactivamos pestaña PDF
    setActiveTab(0);
  };

  const handleDecrementarContador2 = () => {
    if (contadorYear2 > 0) {
      setCount2(contadorYear2 - 1);
      //Reactivamos pestaña PDF
      setActiveTab(0);
    }
  };
  //Contador para mover o simular desplazamiento año en calendario y parametros análisis, inicializando al año fecha sistema (2 Contadores)

  //Pestanyes David
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleTabClick = (tabIndex, nombre, datos) => {
    //Activa penstaña Grafico
    setActiveTab(tabIndex);
    handleSeleccionarParametro({ nombre: nombre, datos: datos });
  };
  //Pestanyes David

  const [tabActivoElementosGeneral, setTabActivoElementosGeneral] = useState(0);

  const handleTabElementosGeneralChange = (event, value) => {
    setTabActivoElementosGeneral(value);
    handleSeleccionarParametro({ nombre: "", datos: "" });
    setAnalisisActivo({});
  };

  const handleTabElementoGeneralClick = (elemento) => {
    setElementoGeneralSeleccionado(elemento);
    handleSeleccionarParametro({ nombre: "", datos: "" });
    GetParametrosFiltradosSinSeleccionarElemento(elemento.id);
  };

  //PopUp David per mostrar incidencia
  const [open, setOpen] = useState(false);

  const handleOpenClosePopUp = () => {
    setOpen(!open);
  };

  //Posicionar al principio página al hacer clic en botón
  const handleInicioPagina = () => {
    //posiciona la página al principio
    window.scroll(0, 0);
  };
  //Posicionar al principio página al hacer clic en botón

  //Posicionar al final página al hacer clic en botón
  const handleScrollToBottom = () => {
    window.scrollTo(0, document.body.scrollHeight);
  };
  //Posicionar al final página al hacer clic en botón

  function filtrarCodigoCliente(cliente) {
    if (!cliente.deleted) {
      if (inputCodigoCliente === "") {
        return true;
      } else {
        if (cliente.codigo?.toString().indexOf(inputCodigoCliente) >= 0) {
          return true;
        } else {
          return false;
        }
      }
    } else {
      return false;
    }
  }

  function filtrarNombreCliente(cliente) {
    if (!cliente.deleted) {
      if (inputNombreCliente === "") {
        return true;
      } else {
        const nombreClienteLowerCase = cliente.razonSocial
          ? cliente.razonSocial.toString().toLowerCase()
          : "";
        const inputNombreClienteLowerCase = inputNombreCliente.toLowerCase();
        return nombreClienteLowerCase.includes(inputNombreClienteLowerCase);
      }
    } else {
      return false;
    }
  }

  // NBG INI: Obtener fechas de la oferta seleccionada para filtrar calendario
  const ofertaActual = ofertas.find(
    (oferta) => oferta.numeroOferta === clienteSeleccionado.oferta
  );
  const fechaInicioOferta = ofertaActual?.fechaInicio || null;
  const fechaFinOferta = ofertaActual?.fechaFinalizacion || null;
  // NBG FIN


  return (
    <>
      {user.idPerfil !== 2 ? (
        <Grid container spacing={2}>
          {/* APARTADO INICIAL, SELECCIÓN DE PLANTA */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Grid container spacing={2} sx={{ alignItems: "center" }}>
                  <Grid item xs={3}>
                    <Typography variant="h6">
                      {plantaActiva.nombreCliente
                        ? plantaActiva.nombreCliente
                        : "Selecciona un cliente"}
                    </Typography>
                  </Grid>
                  <Grid item xs={1}>
                    {plantaActiva.descripcion && (
                      <Typography>{plantaActiva.descripcion}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={2}>
                    <Autocomplete
                      id="nombreCliente"
                      options={clientes}
                      value={
                        clientes.find(
                          (cliente) =>
                            cliente.razonSocial ===
                            clienteSeleccionado.nombreCliente
                        ) || null
                      }
                      filterOptions={(options) =>
                        clientes.filter((cliente) =>
                          filtrarNombreCliente(cliente)
                        )
                      }
                      onInputChange={(event, newInputValue) => {
                        setInputNombreCliente(newInputValue);
                      }}
                      getOptionLabel={(option) => option.razonSocial}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Nombre Cliente"
                          name="nombreCliente"
                        />
                      )}
                      onChange={(event, value) =>
                        setClienteSeleccionado((prevState) => ({
                          ...prevState,
                          nombreCliente: value ? value.razonSocial : null,
                          codigoCliente: value ? parseInt(value.codigo) : null,
                          oferta: "",
                          descripcion: "",
                        }))
                      }
                    />
                  </Grid>

                  <Grid item xs={2}>
                    <Autocomplete
                      id="clientes"
                      options={clientes}
                      value={
                        clientes.find(
                          (cliente) =>
                            cliente.codigo === clienteSeleccionado.codigoCliente
                        ) || null
                      }
                      filterOptions={(options) =>
                        clientes.filter((cliente) =>
                          filtrarCodigoCliente(cliente)
                        )
                      }
                      onInputChange={(event, newInputValue) => {
                        setInputCodigoCliente(newInputValue);
                      }}
                      getOptionLabel={(option) => option.codigo.toString()}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Código Cliente"
                          name="codigoCliente"
                        />
                      )}
                      onChange={(event, value) =>
                        setClienteSeleccionado((prevState) => ({
                          ...prevState,
                          codigoCliente: value ? parseInt(value.codigo) : null,
                          nombreCliente: value ? value.razonSocial : null,
                          oferta: "",
                          descripcion: "",
                        }))
                      }
                    />
                  </Grid>
                  {/* Autocomplete de Descripción */}
                  <Grid item xs={2}>
                    <Autocomplete
                      id="descripcion"
                      options={(() => {
                        // Filtrar ofertas del cliente seleccionado
                        const ofertasCliente = ofertas.filter(
                          (oferta) =>
                            oferta.codigoCliente === clienteSeleccionado.codigoCliente &&
                            !oferta.deleted
                        );

                        // Obtener descripciones únicas
                        const descripcionesUnicas = [...new Set(ofertasCliente.map(o => o.descripcion))];

                        // Devolver array de objetos con descripción
                        return descripcionesUnicas.map(desc => ({ descripcion: desc }));
                      })()}
                      value={
                        clienteSeleccionado.descripcion
                          ? { descripcion: clienteSeleccionado.descripcion }
                          : null
                      }
                      getOptionLabel={(option) => option.descripcion || ""}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Descripción"
                          name="descripcion"
                        />
                      )}
                      onChange={(event, value) =>
                        setClienteSeleccionado((prevState) => ({
                          ...prevState,
                          descripcion: value ? value.descripcion : null,
                          oferta: null, // No seleccionamos oferta automáticamente
                        }))
                      }
                      disabled={!clienteSeleccionado.codigoCliente}
                    />
                  </Grid>

                  {/* Autocomplete de Oferta - MODIFICADO */}
                  <Grid item xs={2}>
                    <Autocomplete
                      id="ofertas"
                      options={ofertas}
                      value={
                        ofertas.find(
                          (oferta) =>
                            oferta.numeroOferta === clienteSeleccionado.oferta
                        ) || null
                      }
                      filterOptions={(options) => {
                        if (
                          clienteSeleccionado.nombreCliente !== "" &&
                          clienteSeleccionado.codigoCliente !== 0 &&
                          clienteSeleccionado.nombreCliente !== null &&
                          clienteSeleccionado.codigoCliente !== null
                        ) {
                          // Filtrar por cliente
                          let filtered = options.filter(
                            (oferta) =>
                              oferta.nombreCliente === clienteSeleccionado.nombreCliente &&
                              oferta.codigoCliente === clienteSeleccionado.codigoCliente &&
                              !oferta.deleted
                          );

                          // Si hay descripción seleccionada, filtrar también por descripción
                          if (clienteSeleccionado.descripcion) {
                            filtered = filtered.filter(
                              (oferta) => oferta.descripcion === clienteSeleccionado.descripcion
                            );
                          }

                          return filtered;
                        } else {
                          return options.filter((oferta) => !oferta.deleted);
                        }
                      }}
                      getOptionLabel={(option) => {
                        /* SI QUEREMOS QUE MUESTRE SUBOFERTA
                        if (option.subOferta && option.subOferta.trim() !== '') {
                          return `${option.numeroOferta}/${option.subOferta}`;
                        }*/
                        return option.numeroOferta.toString();
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Oferta" name="oferta" />
                      )}
                      onChange={(event, value) =>
                        setClienteSeleccionado((prevState) => ({
                          ...prevState,
                          codigoCliente: value ? parseInt(value.codigoCliente) : prevState.codigoCliente,
                          nombreCliente: value ? value.nombreCliente : prevState.nombreCliente,
                          oferta: value ? value.numeroOferta : null,
                          descripcion: value ? value.descripcion : prevState.descripcion,
                        }))
                      }
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* APARTADO DEL DIAGRAMA */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: 0 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ pt: 1, pb: 1, pl: 2 }}>
                      Diagrama de la planta
                    </Typography>
                  </Grid>
                  {/* <Grid item xs={12} sx={{ height: 950 }}> */}
                  <Grid item xs={12} sx={{ height: 600 }}>
                    <FlowWithProvider
                      nodes={nodos}
                      edges={lados}
                      onEdgesChange={onEdgesChange}
                      onNodesChange={onNodesChange}
                      nodeTypes={nodeTypesDashboard}
                      connectionLineComponent={ConnectionLine}
                    ></FlowWithProvider>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Trampilla para posicionar pantalla al hacer clic en elemento del diagrama */}
          <Grid item id="scroll" xs={12}></Grid>
          <Grid item xs={12}></Grid>

          {/* APARTADO CALENDARIO DE TAREAS POR ELEMENTO */}
          <Grid item xs={12}>
            <TareasCalendar
              elementoActivo={elementoActivo}
              analisis={analisis}
              parametrosFiltrados={parametrosFiltrados}
              handleSeleccionarAnalisis={handleSeleccionarAnalisis}
              descargarPdf={descargarPdf}
              handleTabElementosGeneralChange={handleTabElementosGeneralChange}
              handleTabElementoGeneralClick={handleTabElementoGeneralClick}
              tabActivoElementosGeneral={tabActivoElementosGeneral}
              elementosGeneral={elementosGeneral}
              elementoGeneralSeleccionado={elementoGeneralSeleccionado}
              clienteSeleccionado={clienteSeleccionado}
              fechaInicioOferta={fechaInicioOferta}
              fechaFinOferta={fechaFinOferta}
            />

          </Grid>

          {/*Apartado incidencias */}
          <Grid item xs={6}>
            <IncidenciasCard
              elementoActivo={elementoActivo}
              observaciones={observaciones}
              setObservaciones={setObservaciones}
              observacion={observacion}
              setObservacion={setObservacion}
              observacionEditar={observacionEditar}
              setObservacionEditar={setObservacionEditar}
              elementosGeneral={elementosGeneral}
              tabActivoElementosGeneral={tabActivoElementosGeneral}
              handleTabElementosGeneralChange={handleTabElementosGeneralChange}
              handleTabElementoGeneralClick={handleTabElementoGeneralClick}
            />
          </Grid>

          {/* APARTADO DOCUMENTOS */}
          <Grid item xs={6}>
            <Documents
              elementoActivo={elementoActivo}
              user={user}
              plantaActiva={plantaActiva}
              clienteSeleccionado={clienteSeleccionado}
              elementoGeneralSeleccionado={elementoGeneralSeleccionado}
            />
          </Grid>

          {/* FIN APARTADO CALENDARIO DE TAREAS */}
          <Grid container align="right">
            <Grid item xs={12}>
              <IconButton onClick={() => handleScrollToBottom()}>
                <ArrowDownwardIcon fontSize="large" />
              </IconButton>
              <IconButton onClick={() => handleInicioPagina()}>
                <ArrowUpwardIcon fontSize="large" />
              </IconButton>
            </Grid>
          </Grid>

          {/* <Grid item id="scroll-2" xs={12} sx={{ display: 'none' }}></Grid> */}

          {/* APARTADO TABLA DE PARAMETROS */}
          <Grid item xs={6} id="scroll-2">
            <ParametersTable
              analisisActivo={analisisActivo}
              elementoActivo={elementoActivo}
              elementoGeneralSeleccionado={elementoGeneralSeleccionado}
              handleDecrementarContador2={handleDecrementarContador2}
              handleIncrementarContador2={handleIncrementarContador2}
              contadorYear2={contadorYear2}
              parametros={parametros}
              analisisParametros={analisisParametros}
              handleTabClick={handleTabClick}
              parametrosFiltradosNoFQ={parametrosFiltradosNoFQ}
            />
          </Grid>

          {/* APARTADO DOCUMENTOS */}
          <Grid item xs={6}>
            <Graphics
              elementoActivo={elementoActivo}
              parametroActivo={parametroActivo}
              ChartContainer={ChartContainer}
              elementoGeneralSeleccionado={elementoGeneralSeleccionado}
            />
          </Grid>

          <Grid container align="right">
            <Grid item xs={12}>
              <IconButton onClick={() => handleInicioPagina()}>
                <ArrowUpwardIcon fontSize="large" />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={2}>
          {/* APARTADO INICIAL, SELECCIÓN DE PLANTA */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Grid container sx={{ alignItems: "center" }}>
                  <Grid item xs={5}>
                    <Typography variant="h6">
                      {plantaActiva.nombreCliente
                        ? plantaActiva.nombreCliente
                        : "Selecciona un código de oferta"}
                    </Typography>
                  </Grid>
                  <Grid item xs={1}>
                    {plantaActiva.descripcion && (
                      <Typography>{plantaActiva.descripcion}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={2}>
                    <Autocomplete
                      id="descripcion"
                      options={ofertas}
                      value={
                        ofertas.find(
                          (oferta) =>
                            oferta.numeroOferta ===
                            clienteSeleccionado.oferta &&
                            oferta.codigoCliente === user.idCliente
                        ) || null
                      }
                      filterOptions={(options) => {
                        if (
                          clienteSeleccionado.referencia !== "" &&
                          clienteSeleccionado.oferta !== 0 &&
                          clienteSeleccionado.referencia !== null &&
                          clienteSeleccionado.oferta !== null
                        ) {
                          return options.filter(
                            (oferta) =>
                              oferta.referencia ===
                              clienteSeleccionado.referencia &&
                              oferta.numeroOferta ===
                              clienteSeleccionado.oferta &&
                              oferta.codigoCliente === user.idCliente &&
                              !oferta.deleted
                          );
                        } else {
                          return options.filter(
                            (oferta) =>
                              !oferta.deleted &&
                              oferta.codigoCliente === user.idCliente
                          );
                        }
                      }}
                      getOptionLabel={(option) => option.descripcion}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Descripción"
                          name="descripcion"
                        />
                      )}
                      onChange={(event, value) =>
                        setClienteSeleccionado((prevState) => ({
                          ...prevState,
                          codigoCliente: value
                            ? parseInt(value.codigoCliente)
                            : null,
                          nombreCliente: value ? value.nombreCliente : null,
                          descripcion: value ? value.descripcion : null,
                          referencia: value ? value.referencia : null,
                          oferta: value ? value.numeroOferta : null,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <Autocomplete
                      id="referencia"
                      options={ofertas}
                      value={
                        ofertas.find(
                          (oferta) =>
                            oferta.descripcion ===
                            clienteSeleccionado.descripcion &&
                            oferta.codigoCliente === user.idCliente
                        ) || null
                      }
                      filterOptions={(options) => {
                        if (
                          clienteSeleccionado.descripcion !== "" &&
                          clienteSeleccionado.oferta !== 0 &&
                          clienteSeleccionado.descripcion !== null &&
                          clienteSeleccionado.oferta !== null
                        ) {
                          return options.filter(
                            (oferta) =>
                              oferta.descripcion ===
                              clienteSeleccionado.descripcion &&
                              oferta.numeroOferta ===
                              clienteSeleccionado.oferta &&
                              oferta.codigoCliente === user.idCliente &&
                              !oferta.deleted
                          );
                        } else {
                          return options.filter(
                            (oferta) =>
                              !oferta.deleted &&
                              oferta.codigoCliente === user.idCliente
                          );
                        }
                      }}
                      getOptionLabel={(option) => option.referencia}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Referencia"
                          name="referencia"
                        />
                      )}
                      onChange={(event, value) =>
                        setClienteSeleccionado((prevState) => ({
                          ...prevState,
                          codigoCliente: value
                            ? parseInt(value.codigoCliente)
                            : null,
                          nombreCliente: value ? value.nombreCliente : null,
                          descripcion: value ? value.descripcion : null,
                          referencia: value ? value.referencia : null,
                          oferta: value ? value.numeroOferta : null,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <Autocomplete
                      id="ofertas"
                      options={ofertas}
                      value={
                        ofertas.find(
                          (oferta) =>
                            oferta.numeroOferta === clienteSeleccionado.oferta
                        ) || null
                      }
                      filterOptions={(options) => {
                        if (
                          clienteSeleccionado.descripcion !== "" &&
                          clienteSeleccionado.referencia !== "" &&
                          clienteSeleccionado.referencia !== null &&
                          clienteSeleccionado.descripcion !== null
                        ) {
                          return options.filter(
                            (oferta) =>
                              oferta.descripcion ===
                              clienteSeleccionado.descripcion &&
                              oferta.referencia ===
                              clienteSeleccionado.referencia &&
                              oferta.codigoCliente === user.idCliente &&
                              !oferta.deleted
                          );
                        } else {
                          return options.filter(
                            (oferta) =>
                              !oferta.deleted &&
                              oferta.codigoCliente === user.idCliente
                          );
                        }
                      }}
                      getOptionLabel={(option) =>
                        option.numeroOferta.toString()
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Oferta" name="oferta" />
                      )}
                      onChange={(event, value) =>
                        setClienteSeleccionado((prevState) => ({
                          ...prevState,
                          codigoCliente: value
                            ? parseInt(value.codigoCliente)
                            : null,
                          nombreCliente: value ? value.nombreCliente : null,
                          oferta: value ? value.numeroOferta : null,
                          descripcion: value ? value.descripcion : null,
                          referencia: value ? value.referencia : null,
                        }))
                      }
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* APARTADO DEL DIAGRAMA */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: 0 }}>
                <Grid container>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ pt: 1, pb: 1, pl: 2 }}>
                      Diagrama de la planta
                    </Typography>
                  </Grid>
                  {/* <Grid item xs={12} sx={{ height: 950 }}> */}
                  <Grid item xs={12} sx={{ height: 600 }}>
                    <FlowWithProvider
                      nodes={nodos}
                      edges={lados}
                      onEdgesChange={onEdgesChange}
                      onNodesChange={onNodesChange}
                      nodeTypes={nodeTypesDashboard}
                      connectionLineComponent={ConnectionLine}
                    ></FlowWithProvider>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Trampilla para posicionar pantalla al hacer clic en elemento del diagrama */}
          <Grid item id="scroll" xs={12}></Grid>
          <Grid item xs={12}></Grid>
          {/* Trampilla para posicionar pantalla al hacer clic en elemento del diagrama */}

          {/* APARTADO CALENDARIO DE TAREAS POR ELEMENTO */}
          <Grid item xs={12}>
            <TareasCalendar
              elementoActivo={elementoActivo}
              analisis={analisis}
              parametrosFiltrados={parametrosFiltrados}
              handleSeleccionarAnalisis={handleSeleccionarAnalisis}
              descargarPdf={descargarPdf}
              handleTabElementosGeneralChange={handleTabElementosGeneralChange}
              handleTabElementoGeneralClick={handleTabElementoGeneralClick}
              tabActivoElementosGeneral={tabActivoElementosGeneral}
              elementosGeneral={elementosGeneral}
              elementoGeneralSeleccionado={elementoGeneralSeleccionado}
              clienteSeleccionado={clienteSeleccionado}
              fechaInicioOferta={fechaInicioOferta}
              fechaFinOferta={fechaFinOferta}
            />
          </Grid>

          {/*Apartado incidencias */}
          <Grid item xs={6}>
            <IncidenciasCard
              elementoActivo={elementoActivo}
              observaciones={observaciones}
              setObservaciones={setObservaciones}
              observacion={observacion}
              setObservacion={setObservacion}
              observacionEditar={observacionEditar}
              setObservacionEditar={setObservacionEditar}
              elementosGeneral={elementosGeneral}
              tabActivoElementosGeneral={tabActivoElementosGeneral}
              handleTabElementosGeneralChange={handleTabElementosGeneralChange}
              handleTabElementoGeneralClick={handleTabElementoGeneralClick}
            />
          </Grid>

          {/* APARTADO DOCUMENTOS */}
          <Grid item xs={6}>
            <Documents
              elementoActivo={elementoActivo}
              user={user}
              plantaActiva={plantaActiva}
              clienteSeleccionado={clienteSeleccionado}
              elementoGeneralSeleccionado={elementoGeneralSeleccionado}
            />
          </Grid>


          {/* FIN APARTADO CALENDARIO DE TAREAS */}

          <Grid container align="right">
            <Grid item xs={12}>
              <IconButton onClick={() => handleScrollToBottom()}>
                <ArrowDownwardIcon fontSize="large" />
              </IconButton>
              <IconButton onClick={() => handleInicioPagina()}>
                <ArrowUpwardIcon fontSize="large" />
              </IconButton>
            </Grid>
          </Grid>

          {/* APARTADO TABLA DE PARAMETROS */}
          <Grid item xs={6} id="scroll-2">
            <ParametersTable
              analisisActivo={analisisActivo}
              elementoActivo={elementoActivo}
              elementoGeneralSeleccionado={elementoGeneralSeleccionado}
              handleDecrementarContador2={handleDecrementarContador2}
              handleIncrementarContador2={handleIncrementarContador2}
              contadorYear2={contadorYear2}
              parametros={parametros}
              analisisParametros={analisisParametros}
              handleTabClick={handleTabClick}
              parametrosFiltradosNoFQ={parametrosFiltradosNoFQ}
            />
          </Grid>

          {/* APARTADO DOCUMENTOS */}
          <Grid item xs={6}>
            <Graphics
              elementoActivo={elementoActivo}
              parametroActivo={parametroActivo}
              ChartContainer={ChartContainer}
              elementoGeneralSeleccionado={elementoGeneralSeleccionado}
            />
          </Grid>

          <Grid container align="right">
            <Grid item xs={12}>
              <IconButton onClick={() => handleInicioPagina()}>
                <ArrowUpwardIcon fontSize="large" />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default HomeCliente;
